import { type QuestionType } from 'types/sapa';

export const questions: Record<string, QuestionType> = {
  welcome: {
    id: 'welcome',
    text:
      '¡Hola! Te damos la bienvenida a SAPA (Servicio de Atención y Prevención de Conflictos) de Avanta Mynd.\n\n' +
      'Antes que nada, gracias por estar aquí.\n\n' +
      'Este espacio es para ayudarte a identificar si podrías estar viviendo una situación difícil en tu entorno laboral y vamos a acompañarte a gestionar una posible situación de conflicto dentro de tu puesto de trabajo.\n\n' +
      'Para entender bien lo que te ocurre, necesitamos que respondas algunas preguntas. Este proceso no te llevará más de 3 minutos ' +
      'y te recordamos que todo lo que respondas será totalmente confidencial y anónimo para tu empresa.\n\n¿Te parece si empezamos?',
    type: 'single',
    options: [
      { id: 'start_yes', text: 'Sí', value: 'yes' },
      { id: 'start_no', text: 'No, prefiero salir ahora', value: 'no' },
    ],
    nextQuestion: answer => (answer === 'yes' ? 'affected_person' : 'exit'),
  },
  exit: {
    id: 'exit',
    text: 'Entendemos tu decisión. Si en algún momento necesitas ayuda, estaremos aquí para ti.',
    type: 'info',
    nextQuestion: 'end',
  },
  affected_person: {
    id: 'affected_person',
    text: '¿Esta situación que va a describir te está afectando a ti directamente o a alguien más?',
    type: 'single',
    options: [
      { id: 'affected_me', text: 'Me está pasando a mí', value: 'me' },
      { id: 'affected_other', text: 'Le está pasando a otra persona', value: 'other' },
      { id: 'affected_group', text: 'Nos está ocurriendo a un grupo de personas', value: 'group' },
    ],
    nextQuestion: 'situations',
  },
  situations: {
    id: 'situations',
    text: '¿Te has sentido identificado/a con alguna de estas situaciones en tu entorno laboral, ya sea en formato online o presencial y (cuando me está pasando a mi) consideras que a otras personas no les pasa? (Puedes marcar varias)',
    type: 'multiple',
    options: [
      {
        id: 'situation_1',
        text: 'Me excluyen de saludos, reuniones o conversaciones importantes',
        value: 'exclusion',
      },
      {
        id: 'situation_2',
        text: 'Me critican o ridiculizan, a veces delante de otras personas',
        value: 'criticism',
      },
      {
        id: 'situation_3',
        text: 'Han difundido rumores o comentarios malintencionados sobre mí, o hacen críticas a mi trabajo, magnifican mis errores...',
        value: 'rumors_criticism',
      },
      {
        id: 'situation_4',
        text: 'Han difundido videos y/o fotos personales o malintencionados sobre mí',
        value: 'personal_media',
      },
      {
        id: 'situation_5',
        text: 'Recibo tocamientos, roces, pellizcos, … en mi puesto de trabajo',
        value: 'physical_contact',
      },
      {
        id: 'situation_6',
        text: 'Me cambian horarios y tareas sin justificación, o de forma que me perjudica, me dan tareas sin sentido, imposibles de cumplir...',
        value: 'schedule_task_changes',
      },
      {
        id: 'situation_7',
        text: 'Recibo gritos, amenazas o gestos que me hacen sentir incómodo/a',
        value: 'threats_gestures',
      },
      {
        id: 'situation_8',
        text: 'Me separan/aíslan de mi entorno de trabajo/equipo de trabajo',
        value: 'isolation',
      },
      {
        id: 'situation_9',
        text: 'Me dificultan el acceso a la información o me dejan sin ocupación sin causa que lo justifique.',
        value: 'information_access',
      },
      { id: 'situation_none', text: 'Nada de esto me ha pasado (OTRAS)', value: 'none' },
    ],
    hasTextInput: true,
    textInputLabel: 'Otras: Explícanos brevemente',
    nextQuestion: 'who',
  },
  who: {
    id: 'who',
    text: '¿Quién suele tener esas actitudes contigo?',
    type: 'single',
    options: [
      { id: 'who_boss', text: 'Un/a jefe/a, superior o persona con autoridad', value: 'boss' },
      { id: 'who_senior', text: 'Un/a compañero/a con más influencia', value: 'senior_influence' },
      {
        id: 'who_group_team',
        text: 'Un grupo de compañeros/as del mismo equipo de trabajo/departamento que se conocen entre sí',
        value: 'group_team',
      },
      {
        id: 'who_group_other',
        text: 'Una o varias personas de otro equipo/departamento',
        value: 'group_other_dept',
      },
      {
        id: 'who_same_influence',
        text: 'Un compañero/a de mi misma influencia',
        value: 'same_influence',
      },
      {
        id: 'who_junior',
        text: 'Un/a compañero/a con menos influencia',
        value: 'junior_influence',
      },
      { id: 'who_external', text: 'Un cliente/usuario/proveedor externo', value: 'external' },
      { id: 'who_prefer_not', text: 'Prefiero no decirlo', value: 'prefer_not' },
    ],
    nextQuestion: 'duration',
  },
  duration: {
    id: 'duration',
    text: '¿Desde hace cuánto percibes comportamientos negativos que te hacen sentir incómodo/a en tu puesto de trabajo?',
    type: 'single',
    options: [
      { id: 'duration_less_month', text: 'Menos de un mes', value: 'less_month' },
      { id: 'duration_1_3_months', text: 'Entre 1 y 3 meses', value: '1_3_months' },
      { id: 'duration_3_6_months', text: 'Entre 3 y 6 meses', value: '3_6_months' },
      { id: 'duration_more_6_months', text: 'Más de 6 meses', value: 'more_6_months' },
      { id: 'duration_not_clear', text: 'No lo tengo claro', value: 'not_clear' },
    ],
    nextQuestion: 'organizational_change',
  },
  organizational_change: {
    id: 'organizational_change',
    text: '¿Esta situación se ha producido a raíz de algún cambio organizativo?',
    type: 'multiple',
    options: [
      { id: 'change_position', text: 'Me han cambiado de puesto', value: 'position' },
      { id: 'change_superior', text: 'Han cambiado a mi superior', value: 'superior' },
      { id: 'change_roles', text: 'Ha habido un cambio de roles', value: 'roles' },
      {
        id: 'change_new_person',
        text: 'Ha entrado una nueva persona en el equipo',
        value: 'new_person',
      },
      {
        id: 'change_reorganization',
        text: 'Han reorganizado el equipo o departamento',
        value: 'reorganization',
      },
      {
        id: 'change_layoffs',
        text: 'Ha habido despidos o reducción de personal',
        value: 'layoffs',
      },
      {
        id: 'change_objectives',
        text: 'Ha habido un cambio en los objetivos o estrategias del área',
        value: 'objectives',
      },
      {
        id: 'change_policies',
        text: 'Se han implementado nuevas políticas o normas internas',
        value: 'policies',
      },
      {
        id: 'change_conditions',
        text: 'Han cambiado las condiciones de trabajo (horarios, carga laboral, funciones)',
        value: 'conditions',
      },
      {
        id: 'change_direction',
        text: 'Ha habido un cambio en la dirección de la empresa',
        value: 'direction',
      },
      { id: 'change_fusion', text: 'Se ha producido una fusión o adquisición', value: 'fusion' },
      {
        id: 'change_remote',
        text: 'Se ha trasladado el lugar de trabajo o se ha implementado el trabajo remoto',
        value: 'remote',
      },
      { id: 'change_none', text: 'No ha habido ningún cambio reciente', value: 'none' },
    ],
    nextQuestion: 'frequency',
  },
  frequency: {
    id: 'frequency',
    text: '¿Con qué frecuencia suceden estas situaciones?',
    type: 'single',
    options: [
      { id: 'frequency_once', text: 'Ha pasado solo una vez', value: 'once' },
      { id: 'frequency_monthly', text: 'Suceden una o dos veces al mes', value: 'monthly' },
      { id: 'frequency_weekly', text: 'Ocurren cada semana', value: 'weekly' },
      { id: 'frequency_variable', text: 'Es muy variable, no sabría concretar', value: 'variable' },
    ],
    nextQuestion: 'witnesses',
  },
  witnesses: {
    id: 'witnesses',
    text: '¿Otras personas han presenciado estas situaciones?',
    type: 'single',
    options: [
      {
        id: 'witnesses_against',
        text: 'Sí, y han reaccionado apoyando o participando en mi contra',
        value: 'against',
      },
      {
        id: 'witnesses_favor',
        text: 'Sí, y han reaccionado apoyando o participando a mi favor',
        value: 'favor',
      },
      { id: 'witnesses_nothing', text: 'Sí, pero no han hecho nada', value: 'nothing' },
      { id: 'witnesses_not_sure', text: 'No estoy seguro/a', value: 'not_sure' },
      { id: 'witnesses_none', text: 'No hay testigos', value: 'none' },
      { id: 'witnesses_proof', text: 'No hay testigos, pero tengo pruebas', value: 'proof' },
    ],
    nextQuestion: 'location',
  },
  location: {
    id: 'location',
    text: '¿Dónde suelen suceder estas situaciones (presencial u online)?',
    type: 'single',
    options: [
      { id: 'location_work', text: 'En el entorno laboral', value: 'work' },
      {
        id: 'location_outside',
        text: 'Fuera del trabajo, pero con personas del trabajo',
        value: 'outside',
      },
      { id: 'location_both', text: 'En ambos casos', value: 'both' },
      { id: 'location_na', text: 'No aplica a mi caso', value: 'na' },
    ],
    nextQuestion: 'reason',
  },
  reason: {
    id: 'reason',
    text: '¿Por qué crees que se comportan así contigo? ¿Qué crees que hay detrás del comportamiento de esas personas hacia ti? (Puedes seleccionar varias respuestas)',
    type: 'multiple',
    options: [
      {
        id: 'reason_aggressive',
        text: 'Es una forma de relacionarse agresiva en general, creo que tiene que ver con escasas habilidades comunicacionales y formas inadecuadas de relación',
        value: 'aggressive',
      },
      {
        id: 'reason_conflict',
        text: 'Un conflicto interpersonal estancado que aún no se ha resuelto',
        value: 'conflict',
      },
      { id: 'reason_bad_leadership', text: 'Un mal estilo de liderazgo', value: 'bad_leadership' },
      {
        id: 'reason_want_leave',
        text: 'Quieren que me sienta mal o que me vaya de la empresa',
        value: 'want_leave',
      },
      {
        id: 'reason_discrimination',
        text: 'Originado por mi orientación sexual, identidad de género, creencias políticas o religiosas, diversidad funcional, p/maternidad, etnia o nacionalidad, edad, …',
        value: 'discrimination',
      },
      {
        id: 'reason_unknown',
        text: 'No entiendo bien qué buscan, pero me está afectando',
        value: 'unknown',
      },
      {
        id: 'reason_personal_dislike',
        text: 'Creo que le caigo mal, no le gusta como soy o como me comporto',
        value: 'personal_dislike',
      },
      {
        id: 'reason_threat',
        text: 'Me ve/ven como una amenaza para sus intereses dentro de la empresa',
        value: 'threat',
      },
      {
        id: 'reason_dept_conflict',
        text: 'Un conflicto en el departamento que hace tiempo que está y que aún no se ha resuelto',
        value: 'dept_conflict',
      },
      {
        id: 'reason_leadership_no_intervention',
        text: 'Un mal estilo de liderazgo: el/la responsable del departamento no interviene, no pone límites y permite que me falten el respeto o me sienta incómodo/a',
        value: 'leadership_no_intervention',
      },
      {
        id: 'reason_leadership_aggressive',
        text: 'Un mal estilo de liderazgo: el/la responsable del departamento también tiene una actitud agresiva y poco dialogante',
        value: 'leadership_aggressive',
      },
    ],
    nextQuestion: 'organizational_conditions_yn',
  },
  organizational_conditions_yn: {
    id: 'organizational_conditions_yn',
    text: '¿Crees que hay alguna condición organizativa que pueda estar siendo detonadora de estas situación?',
    type: 'single',
    options: [
      { id: 'org_yn_yes', text: 'Sí', value: 'yes' },
      { id: 'org_yn_no', text: 'No', value: 'no' },
    ],
    nextQuestion: answer => (answer === 'yes' ? 'which_conditions' : 'protocol_info'),
  },
  which_conditions: {
    id: 'which_conditions',
    text: 'En caso positivo, ¿cuáles? (Con posibilidad de desplegar explicación o ejemplos)',
    type: 'multiple',
    options: [
      {
        id: 'condition_role_clarity',
        text: 'Falta de claridad en el desempeño de tareas y/o responsabilidades del puesto',
        value: 'role_clarity',
      },
      { id: 'condition_autonomy', text: 'Falta de autonomía', value: 'autonomy' },
      {
        id: 'condition_contradictory',
        text: 'Instrucciones contradictorias que provocan conflictos',
        value: 'contradictory_instructions',
      },
      {
        id: 'condition_job_functions',
        text: 'Falta de explicación de los cometidos y funciones del trabajo',
        value: 'job_functions',
      },
      {
        id: 'condition_relationships',
        text: 'Malas relaciones personales en el trabajo',
        value: 'relationships',
      },
      {
        id: 'condition_communication',
        text: 'Ausencia o ineficacia de los canales de comunicación y participación',
        value: 'communication',
      },
      {
        id: 'condition_supervision',
        text: 'Estilos de supervisión inadecuados (por estar ausente o por ser excesiva)',
        value: 'supervision',
      },
      {
        id: 'condition_work_life',
        text: 'Dificultades para conciliar la vida personal con la laboral',
        value: 'work_life_balance',
      },
      {
        id: 'condition_personal',
        text: 'Creo que es algo personal',
        value: 'personal_issue',
      },
    ],
    nextQuestion: 'protocol_info',
  },
  protocol_info: {
    id: 'protocol_info',
    text: '¿Se os ha informado sobre la existencia de un protocolo de conflictos y su finalidad?',
    type: 'single',
    options: [
      { id: 'protocol_yes', text: 'Sí', value: 'yes' },
      { id: 'protocol_no', text: 'No', value: 'no' },
      { id: 'protocol_dont_remember', text: 'No lo recuerdo', value: 'dont_remember' },
    ],
    nextQuestion: 'conclusion',
  },
  conclusion: {
    id: 'conclusion',
    text:
      'Gracias por tomarte tu tiempo para darnos una visión más clara de tu situación. En cualquier caso, si estás aquí es porque tu situación merece atención.\n\n' +
      'Si nos autorizas a contactar contigo, recabaremos la información de forma más personalizada para poder ayudarte de la mejor forma posible.\n\n' +
      'Si quieres, puedes dejarnos tu teléfono o correo electrónico para que podamos ponernos en contacto contigo.',
    type: 'single',
    options: [],
    hasTextInput: true,
    textInputLabel: 'Teléfono o correo electrónico (opcional)',
    nextQuestion: 'end',
  },
  end: {
    id: 'end',
    text: '',
    type: 'info',
  },
};
